import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlus,
  faPrint,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';

import moment from 'moment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

import { _Object, OrderType } from 'utils/interfaces';
import { orderService, paymentService } from 'services';
import {
  capitalizeFirstLetter,
  closeModal,
  doAmountFormat,
  doDateFormatWithTime,
  generateFormattedAddress,
  getPaymentModeLabel,
  getPaymentStatusLabel,
  printReceipt,
  routes,
} from 'utils';

import {
  Breadcrumbs,
  Button,
  InputField,
  SEOHeader,
  SelectField,
} from 'views/components';
import { useTranslation } from 'react-i18next';
import PrinterAccess from '../sales/printer-access';

function OrderDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { orderId } = useParams();
  const { printerDevice, me } = useSelector((state: _Object) => state.session);
  const [loading, setLoading] = useState<_Object>({
    page: false,
    btn: false,
    pdf: false,
  });
  const [OrderDetails, setOrderDetails] = useState<OrderType>({} as OrderType);
  const [isEditing, setIsEditing] = useState(false);

  const [tempDate, setTempDate] = useState<any>(new Date());

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        navigate(routes.sales, {
          state: { focusProductSearch: true, timestamp: Date.now() },
        });
      } else if (e.key === 'F8') {
        if (OrderDetails?.id || OrderDetails?.order_number) {
          e.preventDefault();
          printReceipt(printerDevice, OrderDetails, me);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, OrderDetails, printerDevice, me]);

  useEffect(() => {
    if (orderId) {
      setLoading({ page: true });
      orderService.getOrderDetails(orderId).then((data: OrderType) => {
        if (data) {
          setOrderDetails(data);
          setLoading({ page: false });
        }
      });
    }
  }, [orderId]);

  const handleRecalculateOrder = () => {
    if (window.confirm('Are you sure? This will recalculate order total.')) {
      setLoading({ pdf: true });
      orderService.recalculateOrder(orderId).then(() => {
        orderService.getOrderDetails(orderId).then((data: OrderType) => {
          if (data) {
            setOrderDetails(data);
            setLoading({ pdf: false });
          }
        });
      });
    }
  };

  const paidBalance = OrderDetails?.payments?.reduce(
    (acc: number, item: { amount: number }) => {
      return acc + item.amount;
    },
    0,
  );

  const dueBalance = OrderDetails.total - paidBalance;

  const formik = useFormik({
    initialValues: {
      amount: '',
      payment_date: '',
      payment_ref_number: '',
      payment_mode: '',
      order_id: orderId,
      status: 'paid',
    },
    enableReinitialize: true,

    validationSchema: Yup.object({
      amount: Yup.number()
        .label('Amount')
        .positive(t('orderDetails.amount_greater_then_zero'))
        .test(
          'isValidAmount',
          t('orderDetails.amount_exceed'),
          function (value: any) {
            const tolerance = 0.0001; // Adjust this value based on your specific needs
            return parseFloat(value) - dueBalance <= tolerance;
          },
        )
        .test(
          'maxDecimalPlaces',
          t('orderDetails.amount_two_decimal'),
          function (value: any) {
            const decimalPlaces = value.toString().split('.')[1]?.length || 0;
            return decimalPlaces <= 2;
          },
        )
        .test(
          'maxThreeDigitAfterDecimal',
          t('orderDetails.amount_three_decimal'),
          function (value: any) {
            const decimalPart = value.toString().split('.')[1];
            return (
              !decimalPart ||
              decimalPart.length <= 2 ||
              decimalPart.slice(2).length === 0
            );
          },
        )
        .required(t('orderDetails.amount_required')),

      payment_date: Yup.string()
        .label('Date')
        .required(t('orderDetails.date_required')),
      payment_mode: Yup.string()
        .label('Payment mode')
        .required(t('orderDetails.payment_mode_required')),
    }),

    onSubmit: (values, { resetForm }) => {
      setLoading({ btn: true });
      paymentService.addPayment(values).then((data: _Object) => {
        setLoading({ btn: false });
        if (data) {
          resetForm();
          closeModal('addPaymentModal');
          setLoading({ page: true });
          orderService.getOrderDetails(orderId).then((data: OrderType) => {
            if (data) {
              setOrderDetails(data);
              setLoading({ page: false });
            }
          });
        }
      });
    },
  });

  const onSave = async () => {
    setLoading({ page: true });
    const res =
      orderId &&
      (await orderService.updateOrder({ created_at: tempDate }, orderId));

    if (res.error === false) {
      orderService.getOrderDetails(orderId).then((data: OrderType) => {
        if (data) {
          setOrderDetails(data);
          setLoading({ page: false });
          setIsEditing(false);
        }
      });
    } else {
      setLoading({ page: false });
    }
  };

  return (
    <>
      <SEOHeader title="Order details" />
      <div className="d-flex justify-content-between position-relative">
        <Breadcrumbs
          trails={[
            {
              label: t('order.orders'),
              path: `${routes.orders}`,
            },
            {
              label: `#${OrderDetails.order_number}`,
              path: '',
            },
          ]}
        />

        {loading.pdf == true && <span className="loader order-loader"></span>}
      </div>

      <div
        className={`confirmed-card card border-0 mb-3 ${loading.page === true ? 'is-loading' : ''}`}
      >
        <div className="card-header bg-white border-0 pt-3 pos-justify pos-between">
          <h3 className="position-relative mb-0">
            {t('orderDetails.order_confirm')}&nbsp;
            <img
              src="/assets/images/success.png"
              width={22}
              height={22}
              alt=""
              className="img-fluid success-icon"
            />
          </h3>
          <ul className="list-inline d-flex align-items-center mb-0">
            <li className="list-inline-item">
              <Button
                className="link text-dark p-0 me-3"
                onClick={() => handleRecalculateOrder()}
              >
                <FontAwesomeIcon icon={faRotate} className="m-0 fs-5" />
              </Button>
            </li>

            {me.client?.enable_receipt_print && (
              <li className="list-inline-item">
                <Button
                  className="link text-dark p-0"
                  onClick={() => printReceipt(printerDevice, OrderDetails, me)}
                >
                  <FontAwesomeIcon icon={faPrint} className="m-0 fs-5" />
                </Button>
              </li>
            )}
          </ul>
        </div>
        <div className="card-body pt-0">
          <div className="row mt-4">
            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <strong>{t('orderDetails.order_number')}</strong>
              <p className="mt-2 mb-0">{OrderDetails.order_number || '-'}</p>
            </div>

            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <div className="flex items-center justify-between">
                <strong className="text-sm font-semibold text-gray-700">
                  {t('orderDetails.order_date')}
                </strong>

                <div className="mt-2 text-sm text-gray-900 d-flex align-items-center position-relative">
                  <span>{doDateFormatWithTime(OrderDetails.created_at)}</span>
                  <Button
                    type="button"
                    onClick={() => {
                      setIsEditing(!isEditing);
                    }}
                    className="text-gray-500 hover:text-blue-600 p-0 d-inline ms-2"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  {isEditing && (
                    <div
                      className="mt-2 flex flex-col gap-2 position-absolute z-1"
                      style={{ top: '20px', width: 'max-content' }}
                    >
                      <DatePicker
                        inline
                        closeOnScroll
                        selected={tempDate || new Date()}
                        onChange={(date: Date | null) => setTempDate(date)}
                        showTimeSelect
                        timeIntervals={15}
                        dateFormat="yyyy-MM-dd HH:mm"
                        className="w-full border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <div
                        className="bg-white"
                        style={{
                          border: '1px solid #aeaeae',
                          marginTop: '-10px',
                          paddingBlock: '10px',
                          display: 'flex',
                          justifyContent: 'center',
                          borderBottomRightRadius: '4px',
                          borderBottomLeftRadius: '4px',
                          zIndex: 1,
                          position: 'relative',
                        }}
                      >
                        <Button
                          onClick={() => onSave()}
                          className="primary py-1 px-2 fs-6 text-capitalize"
                        >
                          Save changes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <strong>{t('orderDetails.payment_status')}</strong>
              <p className="mt-2 mb-0">
                {OrderDetails?.status
                  ? getPaymentStatusLabel(
                      OrderDetails.payment_status === null
                        ? 'paid'
                        : OrderDetails.payment_status,
                    )
                  : '-'}
              </p>
            </div>

            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <strong>{t('orderDetails.customer_note')}</strong>
              <p className="mt-2 mb-0">
                {OrderDetails?.order_note
                  ? capitalizeFirstLetter(OrderDetails.order_note)
                  : '-'}
              </p>
            </div>

            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <strong>{t('orderDetails.billing_details')}</strong>
              <p className="mt-2 mb-0">
                {OrderDetails?.billing
                  ? generateFormattedAddress(OrderDetails?.billing)
                  : '-'}
              </p>
            </div>

            <div className="col-6 col-lg-3 col-xl-2 mb-2 mb-xl-0">
              <strong>{t('customer.customer')}</strong>

              <p className="mt-2 mb-0">
                {capitalizeFirstLetter(OrderDetails?.customer?.full_name)}
              </p>

              <p className="mb-0">
                <a
                  href="tel:+4733378901"
                  className="text-dark text-decoration-none"
                  style={{ fontSize: '13px' }}
                >
                  {OrderDetails?.customer?.mobile_number}
                </a>
              </p>
            </div>
          </div>

          <hr />

          <div className="card border-0 my-3">
            <div className="table-responsive">
              <table className="table table-borderless table-striped order-listing mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th style={{ width: '200px' }}>{t('common.name')}</th>
                    <th style={{ width: '80px' }} className="price">
                      {t('common.price')}
                    </th>
                    <th style={{ width: '100px' }} className="quantity">
                      {t('common.quantity')}
                    </th>
                    <th style={{ width: '80px' }} className="total">
                      {t('common.total')}
                    </th>
                    <th style={{ width: '80px' }} className="tax">
                      {t('common.tax')}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {OrderDetails?.order_items?.map(
                    (item: _Object, i: number) => {
                      return (
                        <tr key={item.id}>
                          <td>{i + 1}</td>
                          <td className="break-spaces">
                            {item?.item_name}
                            <small className="d-block text-secondary">
                              Sku: {item?.item_sku}
                            </small>
                          </td>
                          <td>
                            <strong>
                              {doAmountFormat(item.price ? item.price : 0)}
                            </strong>
                          </td>
                          <td>{item.quantity}</td>
                          <td>
                            <strong>
                              {doAmountFormat(
                                item.subtotal ? item.subtotal : 0,
                              )}
                            </strong>
                          </td>
                          <td>{doAmountFormat(item?.tax ? item?.tax : 0)}</td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <hr />

          <div className="row justify-content-between">
            <div className="col col-md-6 col-lg-6 col-xl-5">
              <p className="d-flex align-items-center">
                {' '}
                <strong className="me-2">{t('orderDetails.payments')}</strong>
                {OrderDetails.payment_status !== 'paid' &&
                  OrderDetails.payment_status !== null && (
                    <button
                      type="button"
                      className="btn btn-transparent p-0 border-0"
                      data-bs-toggle="modal"
                      data-bs-target="#addPaymentModal"
                    >
                      <FontAwesomeIcon
                        icon={faCirclePlus}
                        className="me-0 text-primary"
                      />
                    </button>
                  )}
                <div
                  className="modal fade"
                  id="addPaymentModal"
                  tabIndex={-1}
                  aria-labelledby="addPaymentModalLabel"
                  aria-hidden="true"
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <form
                      className="modal-content"
                      onSubmit={formik.handleSubmit}
                    >
                      <div className="modal-header">
                        <aside>
                          <h4 className="mb-0">
                            {t('orderDetails.add_payment')}
                          </h4>
                        </aside>
                        <button
                          type="button"
                          className="btn-close"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></button>
                      </div>
                      <div className="modal-body">
                        <div className="row">
                          <div className="form-group col-12 col-md-6">
                            <label className="label-form mb-1">
                              {t('common.date')}{' '}
                              {<span className="text-danger">*</span>}
                            </label>

                            <DatePicker
                              dateFormat="MMM dd, yyyy"
                              className={`${formik.touched.payment_date && formik.errors.payment_date && 'invalid'}`}
                              placeholderText="Enter here"
                              onChange={(value: any) => {
                                const selectedDate =
                                  moment(value).format('YYYY-MM-DD');
                                formik.setFieldValue(
                                  'payment_date',
                                  selectedDate,
                                );
                              }}
                              selected={
                                formik.values.payment_date
                                  ? moment(
                                      formik.values.payment_date,
                                      'YYYY-MM-DD',
                                    ).toDate()
                                  : null
                              }
                              maxDate={new Date()}
                            />

                            {formik.touched.payment_date &&
                              formik.errors.payment_date && (
                                <span className="invalid-feedback text-danger d-block mt-1">
                                  {formik.errors.payment_date}
                                </span>
                              )}
                          </div>

                          <InputField
                            label={t('orderDetails.reference_no')}
                            name="payment_ref_number"
                            value={formik.values.payment_ref_number}
                            onChange={formik.handleChange}
                            className="col-12 col-md-6"
                            error={
                              formik.touched.payment_ref_number &&
                              formik.errors.payment_ref_number
                            }
                          />
                          <InputField
                            label={t('common.amount')}
                            type="number"
                            required="true"
                            name="amount"
                            value={formik.values.amount}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            className="col-12 col-md-6"
                            error={
                              formik.touched.amount && formik.errors.amount
                            }
                          />

                          <SelectField
                            name="payment_mode"
                            label={t('checkout.payment_mode')}
                            required="true"
                            className="col-12 col-md-6"
                            value={{ value: formik.values.payment_mode }}
                            onBlur={formik.handleBlur}
                            options={me.client?.payment_methods?.map(
                              (item: string) => ({
                                value: item,
                                label: getPaymentModeLabel(item),
                              }),
                            )}
                            onChange={(e: _Object) => {
                              formik.setFieldValue(
                                'payment_mode',
                                e?.value || '',
                              );
                            }}
                            error={
                              formik.touched.payment_mode &&
                              formik.errors.payment_mode
                            }
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="modal-footer">
                        <Button
                          name={t('common.cancel')}
                          type="button"
                          onClick={() => formik.resetForm()}
                          data-bs-dismiss="modal"
                          className="link text-danger text-decoration-underline"
                        />
                        <Button
                          loading={loading.btn}
                          className="primary"
                          name={t('common.submit')}
                          disabled={
                            !formik.values.amount ||
                            !formik.values.payment_mode ||
                            !formik.values.payment_date ||
                            (formik.touched.amount && formik.errors.amount)
                          }
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </p>
              <div className="table-responsive">
                <table className="table table-bordered table-striped order-payments">
                  <thead>
                    <tr className="h-auto">
                      <th style={{ width: '160px' }}>{t('common.date')}</th>
                      <th style={{ width: '100px' }}>
                        {t('orderDetails.ref_no')} #
                      </th>
                      <th style={{ width: '100px' }}>{t('common.amount')}</th>
                      <th style={{ width: '160px', textAlign: 'left' }}>
                        {t('orderDetails.payment_mode')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="h-auto">
                    {OrderDetails?.payments?.map((item: _Object) => {
                      return (
                        <tr key={item.id} className="h-auto">
                          <td>{doDateFormatWithTime(item.created_at)}</td>
                          <td>
                            {item.payment_ref_number
                              ? capitalizeFirstLetter(item.payment_ref_number)
                              : '-'}
                          </td>
                          <td>{doAmountFormat(item.amount)}</td>
                          <td style={{ textAlign: 'left' }}>
                            {getPaymentModeLabel(item.payment_mode)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="col-sm-4 col-md-4 col-lg-4 col-xl-4 offset-md-0 offset-lg-2 offset-xl-3">
              <ul className="p-0 m-0">
                <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1">
                  {t('orderDetails.item_total')}{' '}
                  <span>{doAmountFormat(OrderDetails?.subtotal || '0')}</span>
                </li>
                {OrderDetails.discount_total !== 0 &&
                  OrderDetails.discount_total !== null && (
                    <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1">
                      {t('checkout.discount')}{' '}
                      <span>{OrderDetails.discount_total}</span>
                    </li>
                  )}
                {OrderDetails?.tax_total !== 0 &&
                  OrderDetails?.tax_total !== null && (
                    <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1">
                      {t('orderDetails.tax')}{' '}
                      <span>{OrderDetails.tax_total || '-'}</span>
                    </li>
                  )}
                <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1">
                  <strong className="fs-5">{t('common.total')}</strong>{' '}
                  <strong className="fs-5">
                    {doAmountFormat(
                      OrderDetails?.total ? OrderDetails?.total : 0,
                    )}
                  </strong>
                </li>
                <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1">
                  <small className="text-secondary">
                    {t('orderDetails.paid_amount')}
                  </small>
                  <small className="text-secondary">
                    {doAmountFormat(Math.round(paidBalance))}
                  </small>
                </li>
                {Math.round(dueBalance) > 0 && (
                  <li className="d-flex justify-content-between align-items-center border-0 px-0 pb-1 balance-due">
                    <strong className="fs-5">
                      {t('orderDetails.balance_due')}
                    </strong>
                    <strong className="fs-5">
                      {doAmountFormat(dueBalance)}
                    </strong>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <PrinterAccess />
    </>
  );
}

export default OrderDetails;
